import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminClient } from '../context/AdminClientContext';
import { useStimuli } from '../hooks/useClientScopedData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ContextDocuments } from '../components/ContextDocuments';
import { 
  Plus, 
  Zap, 
  Trash2, 
  Edit2,
  X,
  FileText,
  Loader2,
  AlertCircle,
  Copy,
  Globe,
  ExternalLink,
  Search,
  TriangleAlert
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';


const SCALE_PRESETS = {
  likert_5: {
    name: 'Likert 5-point',
    scale: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  },
  likert_7: {
    name: 'Likert 7-point',
    scale: ['Strongly Disagree', 'Disagree', 'Somewhat Disagree', 'Neutral', 'Somewhat Agree', 'Agree', 'Strongly Agree']
  },
  yes_no: {
    name: 'Yes/No',
    scale: ['Yes', 'No']
  },
  yes_no_maybe: {
    name: 'Yes/No/Uncertain',
    scale: ['Yes', 'No', 'Uncertain']
  },
  favorability: {
    name: 'Favorability',
    scale: ['Very Unfavorable', 'Unfavorable', 'Neutral', 'Favorable', 'Very Favorable']
  },
  likelihood: {
    name: 'Likelihood',
    scale: ['Very Unlikely', 'Unlikely', 'Neutral', 'Likely', 'Very Likely']
  },
  custom: {
    name: 'Custom Scale',
    scale: []
  }
};

export default function Stimuli() {
  const { user } = useAuth();
  const { selectedClientId, activeClient } = useAdminClient();
  const { data: stimuli, loading, refetch } = useStimuli();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStimulus, setEditingStimulus] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [promptText, setPromptText] = useState('');
  const [scaleType, setScaleType] = useState('likert_5');
  const [customScale, setCustomScale] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [contextDocuments, setContextDocuments] = useState([]);
  // New state for stimulus type
  const [stimulusType, setStimulusType] = useState('closed');
  const [outputLength, setOutputLength] = useState('short');
  const [outputLanguage, setOutputLanguage] = useState('');
  const [questionnaireMode, setQuestionnaireMode] = useState(false);
  const [questionnaireQuestions, setQuestionnaireQuestions] = useState([{ name: '', prompt_text: '' }]);

  // Web Context state
  const [webContextEnabled, setWebContextEnabled] = useState(false);
  const [webContextTopic, setWebContextTopic] = useState('');
  const [webContextRecency, setWebContextRecency] = useState('30');
  const [webContextSourcePolicy, setWebContextSourcePolicy] = useState('high_trust');
  const [webContextMode, setWebContextMode] = useState('snapshot');
  const [webContextPreview, setWebContextPreview] = useState(null);
  const [webContextLoading, setWebContextLoading] = useState(false);
  const [clientWebContextEnabled, setClientWebContextEnabled] = useState(false);

  // Check if client has web context enabled
  useEffect(() => {
    const checkWebContext = async () => {
      // Try activeClient first, then editing stimulus's client_id
      const clientId = activeClient?.id || editingStimulus?.client_id;
      if (!clientId) { setClientWebContextEnabled(false); return; }
      try {
        const res = await api.get(`/admin/clients/${clientId}`);
        setClientWebContextEnabled(res.data?.web_context_enabled || false);
      } catch { setClientWebContextEnabled(false); }
    };
    checkWebContext();
  }, [activeClient, editingStimulus]);

  const resetForm = () => {
    setName('');
    setPromptText('');
    setScaleType('likert_5');
    setCustomScale([]);
    setNewOption('');
    setContextDocuments([]);
    setEditingStimulus(null);
    setStimulusType('closed');
    setOutputLength('short');
    setOutputLanguage('');
    setWebContextEnabled(false);
    setWebContextTopic('');
    setWebContextRecency('30');
    setWebContextSourcePolicy('high_trust');
    setWebContextMode('snapshot');
    setWebContextPreview(null);
  };

  const openDialog = (stimulus = null) => {
    if (stimulus) {
      setEditingStimulus(stimulus);
      setName(stimulus.name);
      setPromptText(stimulus.prompt_text);
      setContextDocuments(stimulus.context_documents || []);
      setStimulusType(stimulus.stimulus_type || 'closed');
      setOutputLength(stimulus.output_length || 'short');
      setOutputLanguage(stimulus.output_language || '');
      
      if (stimulus.stimulus_type === 'open_text') {
        setScaleType('likert_5'); // Default, won't be used
      } else {
        const matchedPreset = Object.entries(SCALE_PRESETS).find(
          ([key, preset]) => JSON.stringify(preset.scale) === JSON.stringify(stimulus.response_scale)
        );
        
        if (matchedPreset) {
          setScaleType(matchedPreset[0]);
        } else {
          setScaleType('custom');
          setCustomScale(stimulus.response_scale || []);
        }
      }
    } else {
      resetForm();
    }

    // Load existing web context if editing
    if (stimulus?.web_context_pack_id) {
      setWebContextEnabled(true);
      setWebContextTopic(stimulus.web_context_topic || '');
      setWebContextRecency(String(stimulus.web_context_recency_days || 30));
      setWebContextSourcePolicy(stimulus.web_context_source_policy || 'high_trust');
      setWebContextMode(stimulus.web_context_mode || 'snapshot');
      // Load the WCP preview
      api.get(`/stimuli/${stimulus.id}/web-context`).then(res => {
        if (res.data?.has_web_context) setWebContextPreview(res.data);
      }).catch(() => {});
    } else {
      setWebContextEnabled(false);
      setWebContextTopic('');
      setWebContextPreview(null);
    }

    setDialogOpen(true);
  };

  const handleWebContextPreview = async () => {
    if (!editingStimulus?.id) {
      toast.error('Save the stimulus first before previewing web context');
      return;
    }
    const topic = webContextTopic || promptText?.substring(0, 200);
    if (!topic) { toast.error('Topic is required'); return; }
    setWebContextLoading(true);
    try {
      const res = await api.post(`/stimuli/${editingStimulus.id}/web-context/preview`, {
        topic,
        recency_days: parseInt(webContextRecency),
        source_policy: webContextSourcePolicy,
      });
      setWebContextPreview(res.data);
      toast.success(`Retrieved ${res.data.claims?.length || 0} claims from ${res.data.sources?.length || 0} sources`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Web context preview failed');
    } finally {
      setWebContextLoading(false);
    }
  };

  const handleWebContextCommit = async () => {
    if (!editingStimulus?.id) return;
    const topic = webContextTopic || promptText?.substring(0, 200);
    setWebContextLoading(true);
    try {
      const res = await api.post(`/stimuli/${editingStimulus.id}/web-context/commit`, {
        topic,
        recency_days: parseInt(webContextRecency),
        source_policy: webContextSourcePolicy,
        mode: webContextMode,
      });
      setWebContextPreview(res.data);
      toast.success('Web context committed to stimulus');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Web context commit failed');
    } finally {
      setWebContextLoading(false);
    }
  };

  const handleRemoveWebContext = async () => {
    if (!editingStimulus?.id) return;
    try {
      await api.delete(`/stimuli/${editingStimulus.id}/web-context`);
      setWebContextPreview(null);
      setWebContextEnabled(false);
      toast.success('Web context removed');
    } catch (err) {
      toast.error('Failed to remove web context');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on stimulus type
    if (stimulusType === 'closed') {
      const responseScale = scaleType === 'custom' ? customScale : SCALE_PRESETS[scaleType].scale;
      
      if (responseScale.length < 2) {
        toast.error('Response scale must have at least 2 options');
        return;
      }
    }

    // Validate admin has selected a specific client for new stimuli (not "all")
    if (!editingStimulus && user?.is_admin && (!selectedClientId || selectedClientId === 'all')) {
      toast.error('Please select a specific client (not "All Clients") before creating a stimulus');
      return;
    }

    setSaving(true);
    
    const payload = {
      name,
      prompt_text: promptText,
      stimulus_type: stimulusType,
      context_documents: contextDocuments
    };

    // Add type-specific fields
    if (stimulusType === 'closed') {
      const responseScale = scaleType === 'custom' ? customScale : SCALE_PRESETS[scaleType].scale;
      payload.response_scale = responseScale;
      payload.scale_type = scaleType;
    } else {
      payload.output_length = outputLength;
      payload.output_language = outputLanguage || null;
    }

    try {
      if (editingStimulus) {
        await api.put(`/stimuli/${editingStimulus.id}`, payload);
        toast.success('Stimulus updated');
      } else {
        const url = user?.is_admin && selectedClientId && selectedClientId !== 'all'
          ? `/stimuli?client_id=${selectedClientId}`
          : `/stimuli`;
        await api.post(url, payload);
        toast.success('Stimulus created');
      }
      setDialogOpen(false);
      resetForm();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stimulus');
    } finally {
      setSaving(false);
    }
  };

  const [cloning, setCloning] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/stimuli/${id}`);
      toast.success('Stimulus deleted');
      setDeleteTarget(null);
      refetch();
    } catch (error) {
      toast.error('Failed to delete stimulus');
    }
  };
  
  const handleClone = async (id) => {
    setCloning(id);
    try {
      await api.post(`/stimuli/${id}/clone`);
      toast.success('Stimulus cloned');
      refetch();
    } catch (error) {
      toast.error('Failed to clone stimulus');
    } finally {
      setCloning(null);
    }
  };

  const addCustomOption = () => {
    if (newOption.trim() && !customScale.includes(newOption.trim())) {
      setCustomScale([...customScale, newOption.trim()]);
      setNewOption('');
    }
  };

  const removeCustomOption = (index) => {
    setCustomScale(customScale.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentScale = scaleType === 'custom' ? customScale : SCALE_PRESETS[scaleType]?.scale || [];

  return (
    <div data-testid="stimuli-page" className="space-y-6">
      {/* Admin Client View Banner */}
      {user?.is_admin && activeClient && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            Viewing as: <span className="font-bold">{activeClient.name}</span>
          </span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Stimuli</h1>
          <p className="text-muted-foreground mt-1">Create survey questions with context documents</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            className="gap-2 border-primary/30 text-primary hover:bg-primary/5" 
            onClick={() => setQuestionnaireMode(true)}
            data-testid="create-questionnaire-btn"
          >
            <FileText className="w-4 h-4" />
            Create Questionnaire
          </Button>
          <Button 
            className="bg-primary hover:bg-primary/90 btn-press" 
            onClick={() => openDialog()}
            data-testid="create-stimulus-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Create Stimulus
          </Button>
        </div>
      </div>

      {/* Stimuli List */}
      {stimuli.length > 0 ? (
        <div className="space-y-4">
          {stimuli.map((stimulus, idx) => (
            <Card 
              key={stimulus.id} 
              className={cn(
                "card-interactive border border-border animate-fade-in",
                `stagger-${(idx % 5) + 1}`
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className={cn(
                      "p-2.5 rounded-md",
                      stimulus.stimulus_type === 'open_text' ? "bg-blue-50 dark:bg-blue-500/15" : "bg-yellow-50 dark:bg-yellow-500/15"
                    )}>
                      <Zap className={cn(
                        "w-5 h-5",
                        stimulus.stimulus_type === 'open_text' ? "text-blue-600 dark:text-blue-400" : "text-yellow-600 dark:text-yellow-400"
                      )} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{stimulus.name}</h3>
                        <Badge variant={stimulus.stimulus_type === 'open_text' ? 'secondary' : 'outline'} className="text-xs">
                          {stimulus.stimulus_type === 'open_text' ? 'Open-ended' : 'Closed-ended'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{stimulus.prompt_text}</p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {stimulus.stimulus_type === 'open_text' ? (
                          <>
                            <span className="scale-pill bg-blue-100 text-blue-700 text-xs">
                              {stimulus.output_length === 'short' ? '1-2 sentences' : 
                               stimulus.output_length === 'medium' ? '3-5 sentences' : '1-2 paragraphs'}
                            </span>
                            {stimulus.output_language && (
                              <span className="scale-pill bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs">
                                {stimulus.output_language}
                              </span>
                            )}
                          </>
                        ) : (
                          stimulus.response_scale?.map((opt, i) => (
                            <span 
                              key={i} 
                              className="scale-pill bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs"
                            >
                              {opt}
                            </span>
                          ))
                        )}
                      </div>
                      {stimulus.context_documents?.length > 0 && (
                        <div className="flex items-center gap-1 mt-3">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span className="text-xs text-blue-600">
                            {stimulus.context_documents.length} context document(s)
                          </span>
                        </div>
                      )}
                      {stimulus.web_context_pack_id && (
                        <div className="flex items-center gap-1 mt-2" data-testid="stimulus-wcp-badge">
                          <Globe className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs text-blue-600 font-medium">Web Context</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDialog(stimulus)}
                      data-testid={`edit-stimulus-${stimulus.id}`}
                    >
                      <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClone(stimulus.id)}
                      disabled={cloning === stimulus.id}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      data-testid={`clone-stimulus-${stimulus.id}`}
                    >
                      {cloning === stimulus.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4" strokeWidth={1.5} />
                      )}
                    </Button>
                    <div className="w-px h-6 bg-border mx-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(stimulus)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      data-testid={`delete-stimulus-${stimulus.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-border">
          <CardContent className="empty-state py-16">
            <Zap className="empty-state-icon" strokeWidth={1} />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mt-2">No stimuli yet</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Create a survey question to simulate population responses</p>
            <Button 
              className="mt-6 bg-primary hover:bg-primary/90" 
              onClick={() => openDialog()}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Stimulus
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingStimulus ? 'Edit Stimulus' : 'Create Stimulus'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label htmlFor="stim-name">Name *</Label>
              <Input
                id="stim-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., 4-Day Workweek Opinion"
                required
                data-testid="stimulus-name-input"
              />
            </div>

            {/* Stimulus Type Selection */}
            <div className="space-y-2">
              <Label>Response Type *</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStimulusType('closed')}
                  className={cn(
                    "flex-1 p-3 rounded-lg border-2 text-left transition-all",
                    stimulusType === 'closed' 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="font-medium text-sm">Closed-ended</div>
                  <div className="text-xs text-slate-500 mt-0.5">Multiple choice with fixed options</div>
                </button>
                <button
                  type="button"
                  onClick={() => setStimulusType('open_text')}
                  className={cn(
                    "flex-1 p-3 rounded-lg border-2 text-left transition-all",
                    stimulusType === 'open_text' 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="font-medium text-sm">Open-ended (Text)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Free-form text responses</div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stim-prompt">Question *</Label>
              <Textarea
                id="stim-prompt"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder={stimulusType === 'open_text' 
                  ? "e.g., What are your thoughts on the proposed 4-day workweek policy?" 
                  : "e.g., How favorable would you be to introducing a 4-day workweek?"}
                rows={3}
                required
                data-testid="stimulus-prompt-input"
              />
            </div>

            {/* Conditional fields based on stimulus type */}
            {stimulusType === 'closed' ? (
              <div className="space-y-2">
                <Label>Response Scale *</Label>
                <Select value={scaleType} onValueChange={setScaleType}>
                  <SelectTrigger data-testid="scale-type-select">
                    <SelectValue placeholder="Select scale type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SCALE_PRESETS).map(([key, preset]) => (
                      <SelectItem key={key} value={key}>{preset.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {scaleType === 'custom' ? (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        placeholder="Add response option"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomOption())}
                        data-testid="custom-option-input"
                      />
                      <Button type="button" variant="outline" onClick={addCustomOption}>
                        Add
                      </Button>
                    </div>
                    {customScale.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 rounded-md bg-slate-50 border border-border">
                        {customScale.map((opt, i) => (
                          <span key={i} className="flex items-center gap-1 scale-pill bg-white border border-border">
                            {opt}
                            <button
                              type="button"
                              onClick={() => removeCustomOption(i)}
                              className="ml-1 text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {currentScale.map((opt, i) => (
                      <span key={i} className="scale-pill bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs">
                        {opt}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Open-ended options */
              <div className="space-y-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-border">
                <div className="space-y-2">
                  <Label>Response Length</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'short', label: 'Short', desc: '1-2 sentences' },
                      { value: 'medium', label: 'Medium', desc: '3-5 sentences' },
                      { value: 'long', label: 'Long', desc: '1-2 paragraphs' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setOutputLength(option.value)}
                        className={cn(
                          "flex-1 p-2 rounded-md border text-center transition-all",
                          outputLength === option.value 
                            ? "border-blue-500 bg-white dark:bg-slate-700 shadow-sm" 
                            : "border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500"
                        )}
                      >
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-slate-500">{option.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="output-language">Response Language (optional)</Label>
                  <Input
                    id="output-language"
                    value={outputLanguage}
                    onChange={(e) => setOutputLanguage(e.target.value)}
                    placeholder="e.g., Italian, Spanish (leave empty for English)"
                  />
                  <p className="text-xs text-slate-500">Leave empty to use English</p>
                </div>
                
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> Open-ended responses are synthetic comments generated by AI simulation. 
                    They will be analyzed for sentiment, themes, and summarized automatically.
                  </p>
                </div>
              </div>
            )}

            {/* Context Documents with PDF Support */}
            <div className="border-t border-border pt-5">
              <ContextDocuments
                documents={contextDocuments}
                onDocumentsChange={setContextDocuments}
              />
            </div>

            {/* Web Context Enrichment */}
            {clientWebContextEnabled && (
              <div className="border-t border-border pt-5 space-y-4" data-testid="web-context-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-medium">Web Context</Label>
                    <Badge variant="outline" className="text-[10px]">Optional</Badge>
                  </div>
                  <Switch
                    checked={webContextEnabled}
                    onCheckedChange={setWebContextEnabled}
                    className="data-[state=checked]:bg-primary"
                    data-testid="web-context-stimulus-toggle"
                  />
                </div>

                {webContextEnabled && (
                  <div className="space-y-3 pl-1">
                    <p className="text-xs text-muted-foreground">
                      Enrich this stimulus with external web knowledge for more realistic simulation.
                    </p>

                    {/* Topic Query */}
                    <div>
                      <Label htmlFor="wc-topic" className="text-xs">Topic Query</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          id="wc-topic"
                          value={webContextTopic}
                          onChange={(e) => setWebContextTopic(e.target.value)}
                          placeholder={promptText ? promptText.substring(0, 80) + '...' : 'Auto-suggested from stimulus text'}
                          className="flex-1"
                          data-testid="web-context-topic"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Leave empty to auto-derive from stimulus text</p>
                    </div>

                    {/* Controls Row */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Recency Window</Label>
                        <Select value={webContextRecency} onValueChange={setWebContextRecency}>
                          <SelectTrigger className="mt-1" data-testid="web-context-recency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">Last 7 days</SelectItem>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="90">Last 90 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Source Policy</Label>
                        <Select value={webContextSourcePolicy} onValueChange={setWebContextSourcePolicy}>
                          <SelectTrigger className="mt-1" data-testid="web-context-source-policy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high_trust">High-trust only</SelectItem>
                            <SelectItem value="broad">Broad web</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Context Mode</Label>
                        <Select value={webContextMode} onValueChange={setWebContextMode}>
                          <SelectTrigger className="mt-1" data-testid="web-context-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="snapshot">Snapshot</SelectItem>
                            <SelectItem value="auto_refresh">Auto-refresh</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Actions */}
                    {editingStimulus && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleWebContextPreview}
                          disabled={webContextLoading}
                          data-testid="web-context-preview-btn"
                        >
                          {webContextLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                          Preview
                        </Button>
                        {webContextPreview && (
                          <Button
                            type="button"
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            onClick={handleWebContextCommit}
                            disabled={webContextLoading}
                            data-testid="web-context-commit-btn"
                          >
                            Commit to Stimulus
                          </Button>
                        )}
                        {webContextPreview?.has_web_context && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveWebContext}
                            className="text-red-600 hover:text-red-700"
                            data-testid="web-context-remove-btn"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    )}
                    {!editingStimulus && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-100">
                        Save the stimulus first, then edit it to add web context.
                      </p>
                    )}

                    {/* Preview Panel */}
                    {webContextPreview && (
                      <div className="space-y-3 bg-slate-50 dark:bg-slate-800/30 rounded-lg p-3 border border-slate-200 dark:border-border" data-testid="web-context-preview-panel">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
                            {webContextPreview.committed ? 'Committed' : 'Preview'}: {webContextPreview.claims?.length || 0} claims from {webContextPreview.sources?.length || 0} sources
                          </p>
                          {webContextPreview.snapshot_hash && (
                            <Badge variant="outline" className="text-[10px]">#{webContextPreview.snapshot_hash?.substring(0, 8)}</Badge>
                          )}
                        </div>

                        {/* Summary */}
                        {webContextPreview.summary && (
                          <div className="text-xs text-slate-600 leading-relaxed max-h-24 overflow-y-auto">
                            {webContextPreview.summary.substring(0, 300)}{webContextPreview.summary.length > 300 ? '...' : ''}
                          </div>
                        )}

                        {/* Claims */}
                        {webContextPreview.claims?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] font-medium text-slate-500 uppercase">Key Claims</p>
                            {webContextPreview.claims.slice(0, 5).map((claim, i) => (
                              <div key={claim.claim_id || i} className="text-xs text-slate-600 pl-2 border-l-2 border-blue-200 py-0.5">
                                {claim.text.substring(0, 120)}{claim.text.length > 120 ? '...' : ''}
                              </div>
                            ))}
                            {webContextPreview.claims.length > 5 && (
                              <p className="text-[10px] text-slate-400">+{webContextPreview.claims.length - 5} more claims</p>
                            )}
                          </div>
                        )}

                        {/* Sources */}
                        {webContextPreview.sources?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] font-medium text-slate-500 uppercase">Sources</p>
                            <div className="flex flex-wrap gap-1">
                              {webContextPreview.sources.slice(0, 8).map((src, i) => (
                                <a
                                  key={i}
                                  href={src.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-white rounded border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-300 transition-colors"
                                >
                                  <ExternalLink className="w-2.5 h-2.5" />
                                  {src.publisher}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !name || !promptText || (stimulusType === 'closed' && currentScale.length < 2)}
                className="bg-primary hover:bg-primary/90"
                data-testid="submit-stimulus-btn"
              >
                {saving ? 'Saving...' : editingStimulus ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Questionnaire Creation Dialog */}
      <Dialog open={questionnaireMode} onOpenChange={setQuestionnaireMode}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="questionnaire-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Create Questionnaire
            </DialogTitle>
            <DialogDescription>
              Add multiple questions. Each will be created as a separate stimulus using the same response scale.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Scale selection */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Response Scale (shared)</label>
              <select 
                className="w-full mt-1 p-2 border rounded-lg text-sm bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                value={scaleType}
                onChange={(e) => setScaleType(e.target.value)}
              >
                {Object.entries(SCALE_PRESETS).map(([key, preset]) => (
                  <option key={key} value={key}>{preset.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                {SCALE_PRESETS[scaleType]?.scale.join(', ')}
              </p>
            </div>

            {/* Questions */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Questions</label>
              {questionnaireQuestions.map((q, idx) => (
                <div key={idx} className="p-3 border border-slate-200 dark:border-border rounded-lg space-y-2 bg-slate-50/50 dark:bg-slate-800/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">Q{idx + 1}</span>
                    {questionnaireQuestions.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                        onClick={() => setQuestionnaireQuestions(qq => qq.filter((_, i) => i !== idx))}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <input
                    className="w-full p-2 border dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-white"
                    placeholder="Question name (e.g. Employee Satisfaction)"
                    value={q.name}
                    onChange={(e) => {
                      const updated = [...questionnaireQuestions];
                      updated[idx].name = e.target.value;
                      setQuestionnaireQuestions(updated);
                    }}
                  />
                  <textarea
                    className="w-full p-2 border dark:border-slate-600 rounded text-sm min-h-[60px] bg-white dark:bg-slate-800 dark:text-white"
                    placeholder="Question text..."
                    value={q.prompt_text}
                    onChange={(e) => {
                      const updated = [...questionnaireQuestions];
                      updated[idx].prompt_text = e.target.value;
                      setQuestionnaireQuestions(updated);
                    }}
                  />
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full gap-1"
                onClick={() => setQuestionnaireQuestions(qq => [...qq, { name: '', prompt_text: '' }])}>
                <Plus className="w-3 h-3" /> Add Question
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 dark:border-border">
            <Button variant="outline" onClick={() => {
              setQuestionnaireMode(false);
              setQuestionnaireQuestions([{ name: '', prompt_text: '' }]);
            }}>Cancel</Button>
            <Button 
              className="bg-primary hover:bg-primary/90"
              disabled={saving || questionnaireQuestions.some(q => !q.name || !q.prompt_text)}
              data-testid="submit-questionnaire-btn"
              onClick={async () => {
                if (user?.is_admin && (!selectedClientId || selectedClientId === 'all')) {
                  toast.error('Please select a specific client first');
                  return;
                }
                setSaving(true);
                try {
                  const responseScale = SCALE_PRESETS[scaleType]?.scale || [];
                  for (const q of questionnaireQuestions) {
                    const payload = {
                      name: q.name,
                      prompt_text: q.prompt_text,
                      stimulus_type: 'closed',
                      response_scale: responseScale,
                      scale_type: scaleType,
                      context_documents: [],
                    };
                    const url = user?.is_admin && selectedClientId && selectedClientId !== 'all'
                      ? `/stimuli?client_id=${selectedClientId}`
                      : `/stimuli`;
                    await api.post(url, payload);
                  }
                  toast.success(`${questionnaireQuestions.length} stimuli created`);
                  setQuestionnaireMode(false);
                  setQuestionnaireQuestions([{ name: '', prompt_text: '' }]);
                  refetch();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to create questionnaire');
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? 'Creating...' : `Create ${questionnaireQuestions.length} Stimuli`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <TriangleAlert className="w-5 h-5 text-red-500" />
              Delete Stimulus
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-stimulus-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
